import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Sparkles, Check, Mail, Phone, ArrowRight, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENSITIES = [
  { value: 'Légère', desc: 'Discrète, à porter au quotidien' },
  { value: 'Modérée', desc: 'Présente sans être envahissante' },
  { value: 'Intense', desc: 'Marquée, qui laisse une trace' },
];

const NOTES = [
  { value: 'Florales', emoji: '🌸' },
  { value: 'Boisées', emoji: '🌳' },
  { value: 'Orientales', emoji: '🪔' },
  { value: 'Fruitées', emoji: '🍑' },
  { value: 'Épicées', emoji: '🌶️' },
  { value: 'Fraîches', emoji: '🍃' },
  { value: 'Gourmandes', emoji: '🍯' },
  { value: 'Musquées', emoji: '✨' },
];

const OCCASIONS = ['Quotidien', 'Travail', 'Soirée', 'Romantique', 'Sport / décontracté'];
const SEASONS = ['Printemps', 'Été', 'Automne', 'Hiver', 'Toute l\'année'];
const AGE_RANGES = ['Moins de 25 ans', '25-34', '35-44', '45-54', '55-64', '65 ans et +'];
const GENDERS = ['Femme', 'Homme', 'Mixte / sans préférence'];
const PERSO_EFFECTS = [
  'Séduire',
  'Me sentir confiant(e)',
  'Évoquer un souvenir',
  'Marquer ma personnalité',
  'Me détendre / cocon',
  'Simplement me faire plaisir',
];

const generateRecommendation = (prefs) => {
  const { intensity = '', notes = [], occasion = '', season = '', gender = '' } = prefs;
  let rec = '✨ Voici une première lecture de votre signature olfactive :\n\n';
  if (notes.includes('Florales') && (intensity === 'Légère' || intensity === 'Modérée')) {
    rec += '• Les Iconiques — florales délicates et intemporelles, idéales pour une signature douce et élégante.\n';
  }
  if (notes.includes('Orientales') || notes.includes('Épicées') || intensity === 'Intense') {
    rec += '• Les Audacieux — fragrances orientales / épicées, puissantes et inoubliables.\n';
  }
  if (notes.includes('Boisées') || notes.includes('Musquées')) {
    rec += '• Les Pépites — boisées chaudes ou musquées raffinées, parfaites pour une signature mature et confidentielle.\n';
  }
  if (notes.includes('Fruitées') || notes.includes('Fraîches') || season === 'Été' || season === 'Printemps') {
    rec += '• Les Iconiques fruitées / Les Pépites fraîches — légères et lumineuses, idéales pour le printemps-été.\n';
  }
  if (notes.includes('Gourmandes')) {
    rec += '• Les Audacieux gourmands — accords vanille / caramel / praliné, ultra-addictifs.\n';
  }
  if (rec === '✨ Voici une première lecture de votre signature olfactive :\n\n') {
    rec += '• Nous explorerons ensemble nos trois gammes pour identifier votre signature exacte.\n';
  }
  rec += '\n👉 La confirmation se fera lors de votre consultation 15 min offerte (en boutique au Polygone de Béziers ou en visio). Nos experts affineront avec vous le parfum exact qui vous correspond.';
  if (occasion) rec += `\n\nUsage prévu : ${occasion}.`;
  if (gender) rec += `\nDestiné à : ${gender}.`;
  return rec;
};

const PersonnalisationPage = () => {
  const [data, setData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    gender: '',
    intensity: '',
    notes: [],
    occasion: '',
    season: '',
    perso_effect: '',
    current_perfume: '',
    allergies: '',
    message: '',
    consent: false,
  });
  const [results, setResults] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleNote = (n) => setData((d) => ({
    ...d,
    notes: d.notes.includes(n) ? d.notes.filter((x) => x !== n) : [...d.notes, n],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email) {
      toast.error('Merci d\'indiquer au moins votre nom et votre email.');
      return;
    }
    if (!data.consent) {
      toast.error('Merci d\'accepter d\'être recontacté(e) pour finaliser votre consultation.');
      return;
    }
    const rec = generateRecommendation(data);
    setResults(rec);
    try {
      setLoading(true);
      await axios.post(`${API}/diagnostic`, {
        name: data.name,
        email: data.email,
        preferences: {
          phone: data.phone,
          age: data.age,
          gender: data.gender,
          intensity: data.intensity,
          notes: data.notes,
          occasion: data.occasion,
          season: data.season,
          perso_effect: data.perso_effect,
          current_perfume: data.current_perfume,
          allergies: data.allergies,
          message: data.message,
        },
        results: rec,
      });
      toast.success('✨ Merci ! Nos experts vous recontactent sous 24h pour votre consultation.');
      setTimeout(() => {
        const el = document.getElementById('diagnostic-results');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'envoi. Réessayez ou appelez-nous au 04 67 21 57 08.');
    } finally {
      setLoading(false);
    }
  };

  const Pill = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full border text-sm transition-all ${
        active
          ? 'bg-[#1E7A6A] text-[#FDFAF4] border-[#1E7A6A] shadow-[0_4px_12px_rgba(30,122,106,0.25)]'
          : 'bg-white text-[#332724] border-[#B8922A]/30 hover:border-[#1E7A6A]'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-20">
      {/* HERO */}
      <div
        className="h-80 bg-cover bg-center flex items-center justify-center relative"
        style={{
          backgroundImage:
            'linear-gradient(rgba(253, 250, 244, 0.55), rgba(253, 250, 244, 0.55)), url(/assets/gallery/matieres_premieres.jpg)',
        }}
      >
        <div className="text-center px-6">
          <Sparkles size={28} className="text-[#B8922A] mx-auto mb-2" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Consultation olfactive en ligne</p>
          <h1
            className="text-5xl md:text-6xl font-light text-[#5C2018] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="personnalisation-title"
          >
            Diagnostic olfactif
          </h1>
          <p className="text-sm text-[#332724]/80 italic max-w-xl mx-auto">
            ~ 3 minutes · réponse personnalisée sous 24h
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <p className="text-base text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
            Quelques questions pour préparer votre rencontre avec nos experts. Vos réponses
            nous permettent d&apos;arriver à votre consultation 15 min offerte (en boutique ou
            en visio) avec une <strong>pré-sélection de fragrances</strong> qui correspondent
            à votre univers.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl border border-[#B8922A]/30 shadow-[0_15px_40px_rgba(92, 32, 24,0.06)] p-6 md:p-10 space-y-10"
          data-testid="diagnostic-form"
        >
          {/* SECTION 1 — Vos coordonnées */}
          <section>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#5C2018] text-[#FDFAF4] flex items-center justify-center text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>1</div>
              <h2 className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Comment vous joindre
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Prénom et nom *"
                value={data.name}
                onChange={(e) => update('name', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
                required
                data-testid="diagnostic-name"
              />
              <input
                type="email"
                placeholder="Email *"
                value={data.email}
                onChange={(e) => update('email', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
                required
                data-testid="diagnostic-email"
              />
              <input
                type="tel"
                placeholder="Téléphone (facultatif)"
                value={data.phone}
                onChange={(e) => update('phone', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
                data-testid="diagnostic-phone"
              />
              <select
                value={data.age}
                onChange={(e) => update('age', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm bg-white"
                data-testid="diagnostic-age"
              >
                <option value="">Tranche d&apos;âge (facultatif)</option>
                {AGE_RANGES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </section>

          {/* SECTION 2 — Profil olfactif */}
          <section>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#5C2018] text-[#FDFAF4] flex items-center justify-center text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>2</div>
              <h2 className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Votre profil olfactif
              </h2>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">Le parfum est destiné à</label>
              <div className="flex gap-2 flex-wrap">
                {GENDERS.map((g) => (
                  <Pill key={g} active={data.gender === g} onClick={() => update('gender', g)} testid={`gender-${g}`}>{g}</Pill>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">Intensité souhaitée</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {INTENSITIES.map((i) => (
                  <button
                    key={i.value}
                    type="button"
                    onClick={() => update('intensity', i.value)}
                    data-testid={`intensity-${i.value.toLowerCase()}`}
                    className={`text-left p-3 rounded-xl border transition-all ${
                      data.intensity === i.value
                        ? 'bg-[#1E7A6A]/10 border-[#1E7A6A] shadow-[0_4px_12px_rgba(30,122,106,0.15)]'
                        : 'bg-white border-[#B8922A]/30 hover:border-[#1E7A6A]/50'
                    }`}
                  >
                    <p className="text-sm font-medium text-[#5C2018] mb-0.5">{i.value}</p>
                    <p className="text-xs text-[#332724]/70 font-light">{i.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">
                Familles olfactives qui vous attirent <span className="text-[#332724]/50 normal-case tracking-normal italic">(plusieurs choix possibles)</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                {NOTES.map((n) => (
                  <Pill
                    key={n.value}
                    active={data.notes.includes(n.value)}
                    onClick={() => toggleNote(n.value)}
                    testid={`note-${n.value.toLowerCase()}`}
                  >
                    <span className="mr-1">{n.emoji}</span>{n.value}
                  </Pill>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">Pour quelle occasion</label>
                <div className="flex gap-2 flex-wrap">
                  {OCCASIONS.map((o) => (
                    <Pill key={o} active={data.occasion === o} onClick={() => update('occasion', o)}>{o}</Pill>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">Saison de prédilection</label>
                <div className="flex gap-2 flex-wrap">
                  {SEASONS.map((s) => (
                    <Pill key={s} active={data.season === s} onClick={() => update('season', s)}>{s}</Pill>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3 — Pour aller plus loin */}
          <section>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#5C2018] text-[#FDFAF4] flex items-center justify-center text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>3</div>
              <h2 className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Votre histoire avec le parfum
              </h2>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-[#332724]/80">Avec ce parfum, je veux avant tout…</label>
              <div className="flex gap-2 flex-wrap">
                {PERSO_EFFECTS.map((p) => (
                  <Pill key={p} active={data.perso_effect === p} onClick={() => update('perso_effect', p)}>{p}</Pill>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <input
                type="text"
                placeholder="Parfum(s) que vous aimez actuellement (facultatif)"
                value={data.current_perfume}
                onChange={(e) => update('current_perfume', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
              />
              <input
                type="text"
                placeholder="Allergies / sensibilités à signaler (facultatif)"
                value={data.allergies}
                onChange={(e) => update('allergies', e.target.value)}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
              />
              <textarea
                placeholder="Un message libre pour nos experts (facultatif) — un souvenir olfactif, une occasion particulière, une attente précise…"
                value={data.message}
                onChange={(e) => update('message', e.target.value)}
                rows={3}
                className="p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm resize-none"
              />
            </div>
          </section>

          {/* Consent */}
          <label className="flex items-start gap-3 p-4 rounded-xl bg-[#FDFAF4] border border-[#B8922A]/20 cursor-pointer">
            <input
              type="checkbox"
              checked={data.consent}
              onChange={(e) => update('consent', e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#1E7A6A]"
              data-testid="diagnostic-consent"
            />
            <span className="text-xs text-[#332724] font-light leading-relaxed">
              J&apos;accepte d&apos;être recontacté(e) par Harmony Parfumerie pour finaliser ma
              consultation olfactive. Mes données sont traitées conformément à la{' '}
              <Link to="/confidentialite" className="text-[#5C2018] underline">politique de confidentialité</Link>.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary px-8 py-4 rounded-full font-medium uppercase tracking-[0.2em] text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="diagnostic-submit"
          >
            {loading ? 'Envoi en cours…' : (<>Envoyer mon diagnostic <ArrowRight size={16} /></>)}
          </button>
          <p className="text-[11px] text-center text-[#332724]/60 italic -mt-4">
            ✨ Confidentiel · Aucune communication commerciale sans votre accord
          </p>
        </form>

        {/* Results */}
        {results && (
          <div
            id="diagnostic-results"
            className="mt-10 bg-gradient-to-br from-[#5C2018] to-[#2E120D] text-[#FDFAF4] p-8 md:p-10 rounded-2xl shadow-[0_20px_50px_rgba(92, 32, 24,0.25)]"
            data-testid="diagnostic-results"
          >
            <div className="flex items-center gap-2 mb-4">
              <Check size={20} className="text-[#B8922A]" />
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A]">Diagnostic reçu</p>
            </div>
            <h3 className="text-3xl mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Votre première lecture
            </h3>
            <pre className="whitespace-pre-wrap font-light leading-relaxed text-sm mb-6 text-[#FDFAF4]/90" style={{ fontFamily: 'inherit' }}>
              {results}
            </pre>
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#FDFAF4]/15">
              <Link
                to="/rendez-vous"
                className="inline-flex items-center justify-center gap-2 bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] transition-colors flex-1"
                data-testid="results-rdv-btn"
              >
                <Heart size={14} /> Réserver ma consultation
              </Link>
              <Link
                to="/boutique/rituels"
                className="inline-flex items-center justify-center gap-2 bg-[#FDFAF4]/10 hover:bg-[#FDFAF4]/20 text-[#FDFAF4] px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] border border-[#FDFAF4]/30 transition-colors flex-1"
              >
                Voir les Rituels <ArrowRight size={14} />
              </Link>
            </div>
            <p className="text-xs text-[#FDFAF4]/70 mt-5 italic">
              📧 Un email récapitulatif vous a été envoyé. Nos experts vous recontactent sous 24h.
            </p>
          </div>
        )}

        {/* Contact alternative */}
        <div className="mt-12 text-center bg-white/60 rounded-2xl p-6 border border-[#B8922A]/20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Vous préférez nous contacter directement ?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a href="tel:0467215708" className="inline-flex items-center gap-2 text-sm text-[#5C2018] hover:text-[#B8922A] transition-colors">
              <Phone size={14} /> 04 67 21 57 08
            </a>
            <span className="text-[#332724]/30 hidden sm:inline">·</span>
            <a href="mailto:contact@maison-harmony.fr" className="inline-flex items-center gap-2 text-sm text-[#5C2018] hover:text-[#B8922A] transition-colors">
              <Mail size={14} /> contact@maison-harmony.fr
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonnalisationPage;
